import { useState } from 'react'
import { useCommitments } from '../../hooks/useCommitments'
import type { CommitmentsData } from '../../hooks/useCommitments'
import { SECTIONS } from '../../data/sections'
import type { SectionId } from '../../types/tools'
import styles from './CommitmentsPanel.module.css'

/** Build sorted tool options for dropdowns */
function getAllToolOptions(): Array<{ id: string; label: string }> {
  const opts: Array<{ id: string; label: string }> = []
  for (const sk of Object.keys(SECTIONS) as SectionId[]) {
    const cats = SECTIONS[sk].cats as unknown as Record<string, { tools: Record<string, { l: string }> }>
    for (const ck of Object.keys(cats)) {
      for (const [tk, tool] of Object.entries(cats[ck].tools)) {
        opts.push({ id: tk, label: tool.l })
      }
    }
  }
  return opts.sort((a, b) => a.label.localeCompare(b.label))
}

function getToolName(toolId: string): string {
  const opts = getAllToolOptions()
  return opts.find((o) => o.id === toolId)?.label ?? toolId
}

/* ─── Setup Mode ─── */
function CommitmentsSetup({ onSave }: { onSave: (data: CommitmentsData) => void }) {
  const [behStart, setBehStart] = useState('')
  const [behStop, setBehStop] = useState('')
  const [behDouble, setBehDouble] = useState('')
  const [toolWeekly, setToolWeekly] = useState('')
  const [toolMonthly, setToolMonthly] = useState('')
  const [toolQuarterly, setToolQuarterly] = useState('')

  const toolOptions = getAllToolOptions()

  const handleSave = () => {
    if (!behStart && !toolWeekly) return
    onSave({
      beh_start: behStart,
      beh_stop: behStop,
      beh_double: behDouble,
      tool_weekly: toolWeekly,
      tool_monthly: toolMonthly,
      tool_quarterly: toolQuarterly,
      beh_start_ratings: [],
      beh_stop_ratings: [],
      beh_double_ratings: [],
      tool_weekly_log: [],
      tool_monthly_log: [],
      tool_quarterly_log: [],
      tool_weekly_ratings: [],
      tool_monthly_ratings: [],
      tool_quarterly_ratings: [],
    })
  }

  return (
    <div data-testid="commitments-setup">
      <div className={styles.rowWrap}>
        <div className={styles.rowHeader}>Row 1: Which behaviours do you want to commit to?</div>
        <div className={styles.row3}>
          <div className={styles.box}>
            <div className={styles.boxLabel} style={{ color: 'var(--blue)' }}>&#9654; Start</div>
            <textarea
              className={styles.input}
              placeholder="One behaviour you will start..."
              value={behStart}
              onChange={(e) => setBehStart(e.target.value)}
              data-testid="setup-beh-start"
            />
          </div>
          <div className={styles.box}>
            <div className={styles.boxLabel} style={{ color: 'var(--blue-dk)' }}>&#9632; Stop</div>
            <textarea
              className={styles.input}
              placeholder="One behaviour you will stop..."
              value={behStop}
              onChange={(e) => setBehStop(e.target.value)}
              data-testid="setup-beh-stop"
            />
          </div>
          <div className={styles.box}>
            <div className={styles.boxLabel} style={{ color: 'var(--orange)' }}>&#9193; Double Down</div>
            <textarea
              className={styles.input}
              placeholder="One behaviour you will double down on..."
              value={behDouble}
              onChange={(e) => setBehDouble(e.target.value)}
              data-testid="setup-beh-double"
            />
          </div>
        </div>
      </div>

      <div className={styles.rowWrap}>
        <div className={styles.rowHeader}>Row 2: Which tools do you want to use?</div>
        <div className={styles.row3}>
          <div className={styles.box}>
            <div className={styles.boxLabel} style={{ color: 'var(--blue)' }}>Weekly</div>
            <select className={styles.select} value={toolWeekly} onChange={(e) => setToolWeekly(e.target.value)} data-testid="setup-tool-weekly">
              <option value="">Select a tool...</option>
              {toolOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          <div className={styles.box}>
            <div className={styles.boxLabel} style={{ color: 'var(--orange)' }}>Monthly</div>
            <select className={styles.select} value={toolMonthly} onChange={(e) => setToolMonthly(e.target.value)} data-testid="setup-tool-monthly">
              <option value="">Select a tool...</option>
              {toolOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          <div className={styles.box}>
            <div className={styles.boxLabel} style={{ color: 'var(--blue-dk)' }}>Quarterly</div>
            <select className={styles.select} value={toolQuarterly} onChange={(e) => setToolQuarterly(e.target.value)} data-testid="setup-tool-quarterly">
              <option value="">Select a tool...</option>
              {toolOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      <button className={styles.saveBtn} onClick={handleSave} data-testid="commitments-save-btn">
        Set My Commitments
      </button>
    </div>
  )
}

/* ─── Rating Dots ─── */
function RatingDots({ ratings }: { ratings: string[] }) {
  if (ratings.length === 0) return null
  return (
    <div className={styles.ratingDots} data-testid="rating-dots">
      {ratings.map((r, i) => (
        <span
          key={i}
          className={styles.ratingDot}
          style={{ background: r === 'G' ? '#4CAF50' : r === 'A' ? '#FF9800' : '#F44336' }}
        />
      ))}
    </div>
  )
}

/* ─── Tracker Mode ─── */
function CommitmentsTracker({
  data,
  onRate,
  onLogTool,
  onEdit,
}: {
  data: CommitmentsData
  onRate: (key: string, rating: string) => void
  onLogTool: (freqKey: string) => void
  onEdit: () => void
}) {
  const behs = [
    { key: 'beh_start', label: '\u25B6 Start', color: 'var(--blue)', val: data.beh_start },
    { key: 'beh_stop', label: '\u25A0 Stop', color: 'var(--blue-dk)', val: data.beh_stop },
    { key: 'beh_double', label: '\u23E9 Double Down', color: 'var(--orange)', val: data.beh_double },
  ]

  const tools = [
    { key: 'weekly', label: 'Weekly', color: 'var(--blue)', val: data.tool_weekly },
    { key: 'monthly', label: 'Monthly', color: 'var(--orange)', val: data.tool_monthly },
    { key: 'quarterly', label: 'Quarterly', color: 'var(--blue-dk)', val: data.tool_quarterly },
  ]

  return (
    <div data-testid="commitments-tracker">
      {/* Behaviours */}
      <div className={styles.rowWrap}>
        <div className={styles.rowHeader}>Which behaviours?</div>
        <div className={styles.row3Plus}>
          {behs.map((b) => {
            const ratings = (data[`${b.key}_ratings` as keyof CommitmentsData] as string[]) ?? []
            return (
              <div className={styles.trackCard} key={b.key} data-testid={`track-${b.key}`}>
                <div className={styles.boxLabel} style={{ color: b.color }}>{b.label}</div>
                <div className={styles.trackValue}>{b.val || '\u2014'}</div>
                <div className={styles.measureLabel}>How am I doing?</div>
                <div className={styles.ragButtons}>
                  <button className={styles.ragGreen} onClick={() => onRate(b.key, 'G')} data-testid={`rag-${b.key}-g`}>G</button>
                  <button className={styles.ragAmber} onClick={() => onRate(b.key, 'A')} data-testid={`rag-${b.key}-a`}>A</button>
                  <button className={styles.ragRed} onClick={() => onRate(b.key, 'R')} data-testid={`rag-${b.key}-r`}>R</button>
                </div>
                <RatingDots ratings={ratings} />
              </div>
            )
          })}
          <div className={styles.changeCol}>
            <div className={styles.changeQ}>Would I like to change my commitment?</div>
            <button className={styles.editBtn} onClick={onEdit} data-testid="commitments-edit-beh">Edit</button>
          </div>
        </div>
      </div>

      {/* Tools */}
      <div className={styles.rowWrap}>
        <div className={styles.rowHeader}>Which tools?</div>
        <div className={styles.row3Plus}>
          {tools.map((t) => {
            const log = (data[`tool_${t.key}_log` as keyof CommitmentsData] as string[]) ?? []
            const ratings = (data[`tool_${t.key}_ratings` as keyof CommitmentsData] as string[]) ?? []
            return (
              <div className={styles.trackCard} key={t.key} data-testid={`track-tool-${t.key}`}>
                <div className={styles.boxLabel} style={{ color: t.color }}>{t.label}</div>
                <div className={styles.trackValue}>{t.val ? getToolName(t.val) : '\u2014'}</div>
                <div className={styles.measureLabel}>Completion log</div>
                <div className={styles.logRow}>
                  <span className={styles.logCount}>{log.length}</span>
                  <button className={styles.doneBtn} onClick={() => onLogTool(t.key)} data-testid={`log-${t.key}`}>
                    &#10003; Done
                  </button>
                </div>
                <div className={styles.measureLabel} style={{ marginTop: 8 }}>How am I doing?</div>
                <div className={styles.ragButtons}>
                  <button className={styles.ragGreen} onClick={() => onRate(`tool_${t.key}`, 'G')} data-testid={`rag-tool-${t.key}-g`}>G</button>
                  <button className={styles.ragAmber} onClick={() => onRate(`tool_${t.key}`, 'A')} data-testid={`rag-tool-${t.key}-a`}>A</button>
                  <button className={styles.ragRed} onClick={() => onRate(`tool_${t.key}`, 'R')} data-testid={`rag-tool-${t.key}-r`}>R</button>
                </div>
                <RatingDots ratings={ratings} />
              </div>
            )
          })}
          <div className={styles.changeCol}>
            <div className={styles.changeQ}>Would I like to change my commitment?</div>
            <button className={styles.editBtn} onClick={onEdit} data-testid="commitments-edit-tools">Edit</button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Main Panel ─── */
export default function CommitmentsPanel() {
  const { data, isLoading, isSetup, save, addRating, logToolCompletion } = useCommitments()
  const [editing, setEditing] = useState(false)

  if (isLoading) {
    return (
      <div className={styles.section} data-testid="commitments-panel-loading">
        <div className={styles.secLabel}>
          <span className={styles.dot} />
          My Commitments
        </div>
        <div className={styles.loadingText}>Loading commitments...</div>
      </div>
    )
  }

  const showSetup = !isSetup || editing

  return (
    <div className={styles.section} data-testid="commitments-panel">
      <div className={styles.secLabel}>
        <span className={styles.dot} />
        My Commitments
      </div>

      {showSetup ? (
        <CommitmentsSetup
          onSave={(newData) => {
            save(newData)
            setEditing(false)
          }}
        />
      ) : (
        <CommitmentsTracker
          data={data!}
          onRate={addRating}
          onLogTool={logToolCompletion}
          onEdit={() => setEditing(true)}
        />
      )}
    </div>
  )
}
